import {
  evaluateGuards, authGuard, trackingGuard, clientGuard, dptGoGuard,
} from '@/router/guards';
import store from '@/store';
import { memberStoreTypes } from '@/store/types';

const defaultFormGuards = [authGuard, trackingGuard, clientGuard];

const virtualPTRoutes = [
  {
    path: 'programs',
    component: () => import(/* webpackChunkName: "VirtualPT" */ '../views/virtual-pt/programs/Programs.vue'),
    beforeEnter: evaluateGuards(defaultFormGuards),
    children: [
      {
        path: '',
        name: 'Programs',
        component: () => import(/* webpackChunkName: "VirtualPT" */ '../views/virtual-pt/programs/Navigator.vue'),
        beforeEnter: evaluateGuards([dptGoGuard]),
      },
      {
        path: ':program/get-started',
        name: 'GetStarted',
        component: () => import(/* webpackChunkName: "VirtualPT" */ '../views/virtual-pt/programs/GetStarted.vue'),
        beforeEnter: (to, from, next) => {
          if (!['dpt', 'bloom'].includes(to.params.program)) {
            return next({
              name: 'Programs',
              params: {
                clientRef: to.params.clientRef,
              },
            });
          }

          return next();
        },
        props: true,
      },
      {
        path: ':program/detail',
        name: 'ProgramDetail',
        component: () => import(/* webpackChunkName: "VirtualPT" */ '../views/virtual-pt/programs/ProgramDetail.vue'),
        props: true,
      },
      {
        path: 'dpt-go',
        name: 'DptGoProgram',
        component: () => import(/* webpackChunkName: "VirtualPT" */ '../views/virtual-pt/programs/DptGoProgram.vue'),
      },
      {
        path: 'download-app',
        name: 'DownloadMoveApp',
        component: () => import(/* webpackChunkName: "VirtualPT" */ '../views/virtual-pt/programs/DownloadApp.vue'),
      },
      {
        path: 'presentation',
        name: 'Wizard',
        component: () => import(/* webpackChunkName: "Wizard" */ '../views/virtual-pt/programs/Wizard.vue'),
        props: true,
      },
    ],
  },
  {
    name: 'VaginalAnatomySelectionForm',
    path: 'vaginal-anatomy',
    component: () => import(/* webpackChunkName: "VaginalAnatomySelection" */ '../views/virtual-pt/VaginalAnatomyStep.vue'),
    meta: {
      stepLabel: 'onboarding2_stepper_about_you',
      trackingName: 'vaginalAnatomy.selection',
    },
    beforeEnter: evaluateGuards(defaultFormGuards),
  },
  {
    name: 'ConditionSelectionForm',
    path: 'condition',
    component: () => import(/* webpackChunkName: "ConditionSelection" */ '../views/virtual-pt/ConditionSelectionStep.vue'),
    meta: {
      stepLabel: 'eco_area_concern',
      trackingName: 'condition.selection',
    },
    beforeEnter: evaluateGuards(defaultFormGuards),
  },
  {
    name: 'DemographicsForm',
    path: 'demographics',
    component: () => import(/* webpackChunkName: "DemographicsInfo" */ '../views/virtual-pt/DemographicsStep.vue'),
    meta: {
      stepLabel: 'onboarding2_stepper_about_you',
      trackingName: 'eligibility.demographicsInfo',
    },
    beforeEnter: evaluateGuards(defaultFormGuards),
    props: true,
  },
  {
    name: 'WorkerCompensationError',
    path: 'demographics/worker-compensation-error',
    props: {
      type: 'generic-error',
      title: 'onboarding2_title_message_thanks',
      text: 'eco_workers_compensation_message',
      buttonText: 'onboarding_go_back',
      routeName: 'Programs',
    },
    component: () => import(/* webpackChunkName: "WorkerCompensationError" */ '../views/feedbacks/GenericFeedbackScreen.vue'),
    beforeEnter: evaluateGuards(defaultFormGuards),
  },
  {
    name: 'ClinicalDecisionForm',
    path: 'kit',
    component: () => import(/* webpackChunkName: "DynamicForms" */ '../views/virtual-pt/DynamicFormsSteps.vue'),
    meta: {
      stepLabel: 'onboarding2_stepper_survey',
      stepProgress: true,
      trackingName: 'condition.clinicalDecision',
    },
    beforeEnter: evaluateGuards(defaultFormGuards),
    props: true,
  },
  {
    name: 'ClinicalExclusionForm',
    path: 'clinical-exclusion',
    component: () => import(/* webpackChunkName: "DynamicForms" */ '../views/virtual-pt/DynamicFormsSteps.vue'),
    meta: {
      stepLabel: 'onboarding2_stepper_survey',
      stepProgress: true,
      trackingName: 'condition.clinicalExclusion',
    },
    beforeEnter: evaluateGuards(defaultFormGuards),
    props: true,
  },
  {
    name: 'RoutineForm',
    path: 'routine',
    component: () => import(/* webpackChunkName: "DynamicForms" */ '../views/virtual-pt/DynamicFormsSteps.vue'),
    meta: {
      stepLabel: 'onboarding2_stepper_survey',
      stepProgress: true,
      trackingName: 'condition.routine',
    },
    beforeEnter: evaluateGuards(defaultFormGuards),
    props: true,
  },
  {
    name: 'SurgeryForm',
    path: 'surgery',
    component: () => import(/* webpackChunkName: "DynamicForms" */ '../views/virtual-pt/DynamicFormsSteps.vue'),
    meta: {
      stepLabel: 'onboarding2_stepper_survey',
      stepProgress: true,
      trackingName: 'survey.painMedicationSurgery',
    },
    beforeEnter: evaluateGuards(defaultFormGuards),
    props: true,
  },
  {
    name: 'EverydayLifeForm',
    path: 'everyday-life',
    component: () => import(/* webpackChunkName: "DynamicForms" */ '../views/virtual-pt/DynamicFormsSteps.vue'),
    meta: {
      stepLabel: 'onboarding2_stepper_survey',
      stepProgress: true,
      trackingName: 'survey.everydayLife',
    },
    beforeEnter: evaluateGuards(defaultFormGuards),
    props: true,
  },
  {
    name: 'AnxietyForm',
    path: 'anxiety',
    component: () => import(/* webpackChunkName: "DynamicForms" */ '../views/virtual-pt/DynamicFormsSteps.vue'),
    meta: {
      stepLabel: 'onboarding2_stepper_survey',
      stepProgress: true,
      trackingName: 'survey.anxiety',
    },
    beforeEnter: evaluateGuards(defaultFormGuards),
  },
  {
    name: 'DepressionForm',
    path: 'depression',
    component: () => import(/* webpackChunkName: "DynamicForms" */ '../views/virtual-pt/DynamicFormsSteps.vue'),
    meta: {
      stepLabel: 'onboarding2_stepper_survey',
      stepProgress: true,
      trackingName: 'survey.depression',
    },
    beforeEnter: evaluateGuards(defaultFormGuards),
    props: true,
  },
  {
    name: 'ProductivityForm',
    path: 'productivity',
    component: () => import(/* webpackChunkName: "DynamicForms" */ '../views/virtual-pt/DynamicFormsSteps.vue'),
    meta: {
      stepLabel: 'onboarding2_stepper_survey',
      stepProgress: true,
      trackingName: 'survey.productivity',
    },
    beforeEnter: evaluateGuards(defaultFormGuards),
    props: true,
  },
  {
    name: 'PainForm',
    path: 'pain',
    component: () => import(/* webpackChunkName: "DynamicForms" */ '../views/virtual-pt/DynamicFormsSteps.vue'),
    meta: {
      stepLabel: 'onboarding2_stepper_survey',
      stepProgress: true,
      trackingName: 'survey.pain',
    },
    beforeEnter: evaluateGuards(defaultFormGuards),
    props: true,
  },
  {
    name: 'CallPreferences',
    path: 'call/options',
    component: () => import(/* webpackChunkName: "CallPreferences" */ '../views/virtual-pt/CallPreferences.vue'),
    meta: {
      stepLabel: 'onboarding2_stepper_call',
      trackingName: 'optionalVideoCallCheck.videoCallPreferences',
    },
  },
  {
    name: 'PainAreas',
    path: 'pain-areas',
    component: () => import(/* webpackChunkName: "ClinicalAssessments" */ '../views/virtual-pt/PainAreasStep.vue'),
    meta: {
      stepLabel: 'step_assessment',
      trackingName: 'clinicalAssessment.painArea',
    },
  },
  {
    name: 'PainDescription',
    path: 'pain-description',
    component: () => import(/* webpackChunkName: "ClinicalAssessments" */ '../views/virtual-pt/PainDescriptionStep.vue'),
    meta: {
      stepLabel: 'step_assessment',
      trackingName: 'clinicalAssessment.painDescription',
    },
  },
  {
    name: 'CallScheduleForm',
    path: 'schedule-call',
    component: () => import(/* webpackChunkName: "ScheduleVideoCall" */ '../views/virtual-pt/ScheduleCallStep.vue'),
    meta: {
      stepLabel: 'onboarding2_stepper_call',
      trackingName: 'videoCall.schedule',
    },
    beforeEnter: async (to, from, next) => {
      try {
        await store.dispatch(memberStoreTypes.actions.GET_MEMBER);
      } finally {
        next();
      }
    },
  },
  {
    name: 'CallRescheduleForm',
    path: 'video-call/reschedule',
    component: () => import(/* webpackChunkName: "ScheduleVideoCall" */ '../views/virtual-pt/ScheduleCallStep.vue'),
    meta: {
      stepLabel: 'onboarding2_stepper_call',
      trackingName: 'videoCall.schedule',
      beforeEnter: evaluateGuards(defaultFormGuards),
    },
  },
  {
    name: 'ConnectionError',
    path: 'video-call/connection-error',
    component: () => import(/* webpackChunkName: "ErrorFeedback" */ '../views/feedbacks/ConnectionError.vue'),
    beforeEnter: evaluateGuards([trackingGuard, authGuard, clientGuard]),
    props: true,
    meta: {
      stepLabel: 'onboarding2_stepper_call',
      trackingName: 'videoCall.schedule',
    },
  },
  {
    name: 'PTSelectionForm',
    path: 'your-physical-therapist',
    component: () => import(/* webpackChunkName: "PTSelection" */ '../views/virtual-pt/PTSelectionStep.vue'),
    meta: {
      stepLabel: 'onboarding2_stepper_choose_pt',
      trackingName: 'ptSelection.selection',
    },
    beforeEnter: evaluateGuards(defaultFormGuards),
  },
  {
    name: 'AddressForm',
    path: 'address',
    component: () => import(/* webpackChunkName: "AddressInfo" */ '../views/virtual-pt/AddressStep.vue'),
    meta: {
      stepLabel: 'onboarding2_stepper_address',
      trackingName: 'address.info',
    },
    beforeEnter: evaluateGuards(defaultFormGuards),
  },
  {
    name: 'VirtualPTSummary',
    path: 'summary',
    component: () => import(/* webpackChunkName: "VirtualPTSummary" */ '../views/virtual-pt/SummaryStep.vue'),
    meta: {
      stepLabel: 'onboarding2_stepper_summary',
      trackingName: 'summary',
    },
    beforeEnter: evaluateGuards(defaultFormGuards),
  },
  {
    name: 'EnrolmentError',
    path: 'error',
    component: () => import(/* webpackChunkName: "ErrorFeedback" */ '../views/feedbacks/ErrorFeedback.vue'),
    beforeEnter: evaluateGuards([trackingGuard, authGuard, clientGuard]),
    props: true,
  },
];

export default virtualPTRoutes;
